import { useQuery } from "@tanstack/react-query";
import { FormSection, ControlledTextArea } from "@excent-one/react/form";
import { FormFieldSet } from "@progress/kendo-react-all";
import { withMemoizedContainer } from "@excent-one/react/utils/children";
import { getAllIncidentFileFieldTypes } from "@phrmpc/infrastructure/phrmpc/api/incident-file-field-types";
import { buildFileFieldTypeMap } from "../utils/fileFieldType";
import {
  INCIDENT_REPORT_ATTACHMENT_ALLOWED_EXTENSIONS,
  INCIDENT_REPORT_ATTACHMENT_MAX_SIZE_BYTES,
  EVIDENCE_ALLOWED_EXTENSIONS,
  EVIDENCE_MAX_SIZE_BYTES,
} from "../utils/uploadLimits";
import IncidentUploadCell from "./IncidentUploadCell";
import type {
  IncidentFormSectionProps,
  IncidentFormValues,
} from "../types";
import type { UseFormSetValue } from "react-hook-form";

interface IncidentNarrativeSectionProps extends IncidentFormSectionProps {
  setValue: UseFormSetValue<IncidentFormValues>;
}

/**
 * The "Incident Details" section: Incident Report Attachment, What Happened?, Evidence
 * (description + upload), Witnesses, Money Involved. Fetches its own fileFieldTypes lookup rather
 * than receiving it from the page - nothing else on the page needs it once MonitoringCaseSection
 * (Part 3) owns its own copy for the case-document chain. Must be rendered under an
 * IncidentRecordFileProvider (via IncidentUploadCell).
 */
const IncidentNarrativeSection = withMemoizedContainer(
  FormSection,
  (
    { control, setValue, incidentRecordId, readOnly, loading, ...sectionProps }: IncidentNarrativeSectionProps,
    Container,
  ) => {
    const { data: fileFieldTypes = [] } = useQuery({
      queryKey: ["incident-file-field-types"],
      queryFn: () => getAllIncidentFileFieldTypes(),
    });
    const fileFieldType = buildFileFieldTypeMap(fileFieldTypes);

    return (
      <Container loading={loading} {...sectionProps}>
        <FormFieldSet cols={1}>
          <IncidentUploadCell
            control={control}
            setValue={setValue}
            name="incidentReportAttachment"
            incidentRecordId={incidentRecordId}
            fileFieldTypeId={fileFieldType.INCIDENT_REPORT_ATTACHMENT}
            disabled={readOnly}
            loading={loading}
            required
            label="Incident Report Attachment"
            allowedExtensions={INCIDENT_REPORT_ATTACHMENT_ALLOWED_EXTENSIONS}
            maxFileSize={INCIDENT_REPORT_ATTACHMENT_MAX_SIZE_BYTES}
            widthPercent={40}
            // Bug 157821 follow-up: SubmitIncidentRecordValidator.cs keys the "must be uploaded
            // before submitting" 400 to Payload.IncidentRecordFiles (a collection-level check,
            // since there's no per-field-type slot on the DTO) - surface it here since this is
            // the only field on the page that check is actually about.
            extraErrorNames={["incidentRecordFiles"]}
          />
          <ControlledTextArea
            name="incidentDescription"
            control={control}
            loading={loading}
            label="What Happened?"
            readOnly={readOnly}
            required={!readOnly}
          />
          <ControlledTextArea
            name="physicalEvidence"
            control={control}
            loading={loading}
            label="What physical evidence or document exists to support your report?"
            readOnly={readOnly}
            required={!readOnly}
          />
          <IncidentUploadCell
            control={control}
            setValue={setValue}
            name="evidence"
            incidentRecordId={incidentRecordId}
            fileFieldTypeId={fileFieldType.EVIDENCE}
            disabled={readOnly}
            loading={loading}
            label="Evidence"
            multiple
            allowedExtensions={EVIDENCE_ALLOWED_EXTENSIONS}
            maxFileSize={EVIDENCE_MAX_SIZE_BYTES}
            widthPercent={40}
          />
          <ControlledTextArea
            name="witnessDetails"
            control={control}
            loading={loading}
            label="How did you know the incident and who are the possible witnesses?"
            readOnly={readOnly}
            required={!readOnly}
          />
          <ControlledTextArea
            name="moneyInvolved"
            control={control}
            loading={loading}
            label="Is there money involved? If yes, please specify the estimated amount."
            readOnly={readOnly}
            required={!readOnly}
          />
        </FormFieldSet>
      </Container>
    );
  },
);

export default IncidentNarrativeSection;
